import { Component } from '@theme/component';

/**
 * Returns shoppers to the purchase options after the original CTA scrolls away.
 * @extends {Component<{buyButton: HTMLButtonElement}>}
 */
class EditableStickyBuy extends Component {
  requiredRefs = ['buyButton'];
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {MutationObserver | undefined} */
  #dialogObserver;
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {ResizeObserver | undefined} */
  #heightObserver;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLElement | null} */
  #mainCTA = null;
  /** @type {HTMLElement | null} */
  #footer = null;
  #frame = 0;
  #returning = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const form = document.getElementById(this.dataset.formId ?? '');
    if (!(form instanceof HTMLFormElement)) return;
    this.#form = form;
    this.#mainCTA = form.querySelector('[data-main-buy-button]');
    this.#footer = document.querySelector('footer, [class*="footer-group"]');

    window.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    document.addEventListener('dialog:open', this.#scheduleUpdate, { signal });
    document.addEventListener('dialog:close', this.#scheduleUpdate, { signal });

    this.#dialogObserver = new MutationObserver(this.#scheduleUpdate);
    this.#dialogObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    this.#visibilityObserver = new IntersectionObserver(this.#scheduleUpdate);
    if (this.#mainCTA) this.#visibilityObserver.observe(this.#mainCTA);
    if (this.#footer) this.#visibilityObserver.observe(this.#footer);
    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(form);
    // The page pads its bottom by the bar's real height (copy, safe area) while it shows.
    this.#heightObserver = new ResizeObserver(this.#syncHeight);
    this.#heightObserver.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#dialogObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#heightObserver?.disconnect();
    document.documentElement.style.removeProperty('--eps-sticky-height');
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#returning = false;
  }

  handleBuyClick() {
    if (!this.#form) return;
    this.#returning = true;
    this.hidden = true;

    // Focus the purchase area, never the submit button or its commerce handlers.
    const hadTabIndex = this.#form.hasAttribute('tabindex');
    if (!hadTabIndex) {
      this.#form.setAttribute('tabindex', '-1');
      this.#form.addEventListener('blur', () => this.#form?.removeAttribute('tabindex'), {
        once: true, signal: this.#controller?.signal,
      });
    }
    this.#form.focus({ preventScroll: true });

    // The custom glass menu floats below a top gap; include its whole bottom edge.
    const header = document.querySelector('.liquid-glass-menu[data-fixed="true"], header-component[sticky]');
    const headerBottom = header?.getBoundingClientRect().bottom ?? 0;
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
    const offset = Math.max(0, headerBottom, headerHeight) + 24;
    const top = Math.max(0, window.scrollY + this.#form.getBoundingClientRect().top - offset);
    window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    this.#scheduleUpdate();
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    if (!this.#mainCTA) {
      this.hidden = true;
      return;
    }
    const rect = this.#mainCTA.getBoundingClientRect();
    if (rect.bottom > 0) this.#returning = false;
    const footerRect = this.#footer?.getBoundingClientRect();
    const footerVisible = footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0;
    const dialogOpen = Boolean(document.querySelector('dialog[open], details[scroll-lock][open], details.liquid-glass-menu__mobile-menu[open]'));
    const mobile = window.matchMedia('(max-width: 989px)').matches;
    const enabled = this.dataset[mobile ? 'mobile' : 'desktop'] === 'true';
    const show = enabled && !this.#returning && rect.bottom <= 0 && rect.height > 0 && !footerVisible && !dialogOpen;
    if (!show && !dialogOpen && document.activeElement === this.refs.buyButton) {
      this.#form?.querySelector('button[ref="addToCartButton"]')?.focus({ preventScroll: true });
    }
    this.hidden = !show;
    if (show) this.#syncHeight();
  }

  #syncHeight = () => {
    if (this.offsetHeight > 0) {
      document.documentElement.style.setProperty('--eps-sticky-height', `${this.offsetHeight}px`);
    }
  };
}

if (!customElements.get('editable-sticky-buy')) {
  customElements.define('editable-sticky-buy', EditableStickyBuy);
}
